import React, { useState, useEffect, useContext } from 'react'
import { useLocation, useHistory } from 'react-router-dom'
import { SystemMessageContext } from './SystemMessageContext'

const AuthContext = React.createContext()
const PageListContext = React.createContext()

// Custom Hook to access auth context
export function useAuth() {
    return useContext(AuthContext)
}

export function usePageList() {
    return useContext(PageListContext)
}

// Custom hook to for protected pages. If authorization fails, it will go to the /noAccess page
export function useProtectedPage(permissions, page, customRedirect = false) {
    let history = useHistory()
    let location = useLocation()

    const { setPageList } = usePageList()
    const [currentPage, setCurrentPage] = useState(location.pathname)

    useEffect(() => {
        // If the page changed, clear the list before adding new permissions
        if (location.pathname !== currentPage) {
            setPageList([])  // Reset correctly before adding new items
            setCurrentPage(location.pathname)
        }
    }, [location.pathname])

    useEffect(() => {
        setPageList(prev => {
            // Prevent adding the same page more than once
            if (!prev.some(p => p.protected_content_name === page)) {
                return [...prev, { protected_content_name: page, location: location.pathname, type: 'page' }]
            }
            return prev
        })

        if (!permissions[page] || !permissions[page].view) {
            if (customRedirect) {
                history.push(customRedirect)
            } else {
                history.push('/noAccess')
            }
        }
    }, [page, permissions, history])
}


// Custom hook to for protected content. Will return true or false. To be used to show/hide content.
export function useProtectedContent(permissions, page) {    
    let location = useLocation()

    const { setPageList } = usePageList()
    const [currentPage, setCurrentPage] = useState(location.pathname)

    useEffect(() => {
        // If the page changed, clear the list before adding new permissions
        if (location.pathname !== currentPage) {
            setPageList([])  // Reset correctly before adding new items
            setCurrentPage(location.pathname)
        }
    }, [location.pathname])

    useEffect(() => {
        setPageList(prev => {
            // Prevent adding the same page more than once
            if (!prev.some(p => p.protected_content_name === page)) {
                return [...prev, { protected_content_name: page, location: location.pathname, type: 'content' }]
            }
            return prev
        })
    }, [page])    

    if (!permissions[page] || !permissions[page].view) {
        return false
    }
    return true
}


// Custom hook to for protected content. Will return true or false. To be used to show/hide editable form fields.
export function useEditableContent(permissions, page) {
    if (!permissions[page] || !permissions[page].edit) {
        return false
    }
    return true
}

// Custom hook to for protected content. Will return true or false. To be used specifically for set manager dropdown where required.
export function useSetManager(permissions, page) {
    if (!permissions[page] || !permissions[page].set_manager) {
        return false
    }
    return true
}

export function AuthProvider({ children, administrator, permissions }) {
    const [user, setUser] = useState({
        user: administrator,
        permissions: permissions,
    })

    useEffect(() => {
        setUser({
            user: administrator,
            permissions: permissions,
        })
    }, [administrator, permissions])

    return <AuthContext.Provider value={user}>{children}</AuthContext.Provider>
}

export const PageListProvider = ({ children }) => {
    let [pageList, setPageList] = useState([])
    return <PageListContext.Provider value={{ pageList, setPageList }}>{children}</PageListContext.Provider>
}
